<template>
  <div>
    <block v-if="loading" />
    <v-card flat class="pa-4 white border-boxGlobal">
      <v-row class="mt-2">
        <v-col cols="12" md="6">
          <v-radio-group v-model="dataType" hide-details class="mt-0">
            <v-radio label="Ver reporte con datos de prueba" value="00" />
            <v-radio label="Ver reporte con datos de producción" value="01" />
          </v-radio-group>
        </v-col>
      </v-row>

      <v-row class="mt-6">
        <v-col cols="12" md="3">
          <DatePicker
            label="Fecha inicio"
            :value="startDate"
            :disbaled="loading"
            @dateSelected="(value) => (startDate = value)"
          />
        </v-col>

        <v-col cols="12" md="3">
          <DatePicker
            label="Fecha fin"
            :value="endDate"
            :disbaled="loading"
            @dateSelected="(value) => (endDate = value)"
          />
        </v-col>

        <v-col cols="12" md="4">
          <v-select
            v-model="selectedBranch"
            :items="branches"
            item-text="BranchName"
            item-value="ID_Branch"
            label="Sucursal"
            :hint="currentBranchLabel"
            persistent-hint
            dense
            outlined
            :disabled="branches.length <= 1"
          />
        </v-col>

        <v-col cols="12" md="2">
          <v-btn block color="primary" :loading="loading" @click="loadReport">
            <v-icon class="mr-1">mdi-magnify</v-icon>
            Buscar
          </v-btn>
        </v-col>
      </v-row>

      <v-row class="mt-1">
        <v-col cols="12">
          <v-checkbox
            v-model="vistaDetallada"
            label="Vista detallada"
            hide-details
            class="mt-0"
          />
        </v-col>
      </v-row>

      <v-alert
        v-if="!loading && rows.length === 0"
        type="info"
        dense
        outlined
        class="mt-4"
      >
        No se encontraron comprobantes para los filtros seleccionados.
      </v-alert>

      <div class="report-grid-wrapper mt-4">
        <DxDataGrid
          ref="salesHotelGrid"
          :data-source="rows"
          :show-borders="true"
          :show-row-lines="true"
          :row-alternation-enabled="true"
          :column-auto-width="true"
          :word-wrap-enabled="true"
          :scrolling="{ useNative: true }"
          :grouping="{ autoExpandAll: true }"
          :group-panel="{ visible: false }"
          @exporting="onExporting"
        >
          <template #groupDateTemplate="{ data }">
            <span>{{ formatGroupTotalDate(data?.value) }}</span>
          </template>
          <DxExport :enabled="true" :formats="['xlsx']" />

          <DxColumn
            data-field="Fecha"
            caption="Fecha"
            data-type="date"
            format="dd/MM/yyyy"
            :group-index="0"
            sort-order="asc"
            :width="120"
            group-cell-template="groupDateTemplate"
          />
          <DxColumn data-field="CodigoGeneracion" caption="Código de generación" :width="180" />
          <DxColumn data-field="DTE" caption="DTE / número de control" :width="170" />
          <DxColumn data-field="SelloRecepcion" caption="Sello de recepción" :width="180" />
          <DxColumn v-if="showDetailedView" data-field="TipoComprobante" caption="Tipo de comprobante" :width="150" />

          <DxColumn v-if="alojamientoPaymentColumns.length" caption="Alojamiento">
            <DxColumn
              v-for="column in alojamientoPaymentColumns"
              :key="column.field"
              :data-field="column.field"
              :caption="column.caption"
              alignment="right"
              :customize-text="formatCurrencyCell"
              :width="140"
            />
          </DxColumn>

          <DxColumn v-if="consumoPaymentColumns.length" caption="Consumo">
            <DxColumn
              v-for="column in consumoPaymentColumns"
              :key="column.field"
              :data-field="column.field"
              :caption="column.caption"
              alignment="right"
              :customize-text="formatCurrencyCell"
              :width="140"
            />
          </DxColumn>
          <DxColumn data-field="FormaPago" caption="Forma de pago" :width="240" />
          <DxColumn v-if="showDetailedView" data-field="TotalAlojamiento" caption="Total alojamiento" alignment="right" :customize-text="formatCurrencyCell" :width="140" />
          <DxColumn v-if="showDetailedView" data-field="TotalConsumo" caption="Total consumo" alignment="right" :customize-text="formatCurrencyCell" :width="140" />
          <DxColumn v-if="showDetailedView" data-field="Exenta" caption="Exentas" alignment="right" :customize-text="formatCurrencyCell" :width="120" />
          <DxColumn v-if="showDetailedView" data-field="NoSujeta" caption="No sujetas" alignment="right" :customize-text="formatCurrencyCell" :width="120" />
          <DxColumn v-if="showDetailedView" data-field="NoGravada" caption="No gravadas" alignment="right" :customize-text="formatCurrencyCell" :width="120" />
          <DxColumn v-if="showDetailedView" data-field="IVA" caption="IVA" alignment="right" :customize-text="formatCurrencyCell" :width="110" />
          <DxColumn v-if="showDetailedView" data-field="ImpuestoTurismo" caption="Impuesto turismo" alignment="right" :customize-text="formatCurrencyCell" :width="140" />
          <DxColumn data-field="Total" caption="Total" alignment="right" :customize-text="formatCurrencyCell" :width="120" />
          <DxPaging :page-size="20" />
          <DxPager :showPageSizeSelector="true" :allowedPageSizes="[10, 20, 50]" />

          <DxSummary>
            <DxGroupItem v-if="showDetailedView" column="TotalAlojamiento" summary-type="sum" :show-in-group-footer="true" :value-format="currencySummaryFormat" display-format="{0}" />
            <DxGroupItem v-if="showDetailedView" column="TotalConsumo" summary-type="sum" :show-in-group-footer="true" :value-format="currencySummaryFormat" display-format="{0}" />
            <DxGroupItem v-if="showDetailedView" column="Exenta" summary-type="sum" :show-in-group-footer="true" :value-format="currencySummaryFormat" display-format="{0}" />
            <DxGroupItem v-if="showDetailedView" column="NoSujeta" summary-type="sum" :show-in-group-footer="true" :value-format="currencySummaryFormat" display-format="{0}" />
            <DxGroupItem v-if="showDetailedView" column="NoGravada" summary-type="sum" :show-in-group-footer="true" :value-format="currencySummaryFormat" display-format="{0}" />
            <DxGroupItem v-if="showDetailedView" column="IVA" summary-type="sum" :show-in-group-footer="true" :value-format="currencySummaryFormat" display-format="{0}" />
            <DxGroupItem v-if="showDetailedView" column="ImpuestoTurismo" summary-type="sum" :show-in-group-footer="true" :value-format="currencySummaryFormat" display-format="{0}" />
            <DxGroupItem column="Total" summary-type="sum" :show-in-group-footer="true" :value-format="currencySummaryFormat" display-format="{0}" />
            <DxGroupItem
              v-for="column in alojamientoPaymentColumns"
              :key="`group-aloj-${column.field}`"
              :column="column.field"
              summary-type="sum"
              :show-in-group-footer="true"
              :value-format="currencySummaryFormat"
              display-format="{0}"
            />
            <DxGroupItem
              v-for="column in consumoPaymentColumns"
              :key="`group-cons-${column.field}`"
              :column="column.field"
              summary-type="sum"
              :show-in-group-footer="true"
              :value-format="currencySummaryFormat"
              display-format="{0}"
            />
          </DxSummary>
        </DxDataGrid>
      </div>
    </v-card>
  </div>
</template>

<script>
import Block from "@/components/Block";
import DatePicker from "@/components/DatePicker.vue";
import { numberWithCommas } from "@/helpers/money";
import { printToExcel } from "@/helpers/printToexcel";
import {
  DxDataGrid,
  DxColumn,
  DxExport,
  DxPaging,
  DxPager,
  DxSummary,
  DxGroupItem,
} from "devextreme-vue/data-grid";

export default {
  name: "SalesHotelReport",
  components: {
    Block,
    DatePicker,
    DxDataGrid,
    DxColumn,
    DxExport,
    DxPaging,
    DxPager,
    DxSummary,
    DxGroupItem,
  },
  data() {
    const today = new Date();
    const todayDisplay = `${String(today.getDate()).padStart(2, "0")}/${String(today.getMonth() + 1).padStart(2, "0")}/${today.getFullYear()}`;

    return {
      loading: false,
      rows: [],
      alojamientoPaymentColumns: [],
      consumoPaymentColumns: [],
      branches: [],
      selectedBranch: null,
      currentBranchName: "",
      dataType: "01",
      vistaDetallada: false,
      startDate: todayDisplay,
      endDate: todayDisplay,
      currency: JSON.parse(localStorage.getItem("branch"))?.Currency || "$",
    };
  },
  computed: {
    showDetailedView() {
      return this.vistaDetallada;
    },
    currentBranchLabel() {
      if (this.selectedBranch === "all") {
        return "Todas las sucursales";
      }

      return this.currentBranchName || "";
    },
  },
  async mounted() {
    await this.loadBranches();
    await this.loadReport();
  },
  watch: {
    dataType() {
      this.loadReport();
    },
    selectedBranch() {
      this.updateCurrentBranchLabel();
    },
  },
  methods: {
    parseDisplayDate(value) {
      if (!value) {
        return null;
      }

      const normalized = String(value).trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
        return normalized;
      }

      const parts = normalized.split("/");
      if (parts.length !== 3) {
        return normalized;
      }

      const [day, month, year] = parts;
      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    },
    formatCurrencyValue(value) {
      const numericValue = Number(value ?? 0);
      if (Number.isNaN(numericValue)) {
        return `${this.currency}0.00`;
      }

      return `${this.currency}${numberWithCommas(numericValue.toFixed(2))}`;
    },
    formatCurrencyCell(cellInfo) {
      return this.formatCurrencyValue(cellInfo?.value);
    },
    currencySummaryFormat(value) {
      if (value === null || value === undefined) {
        return "0.00";
      }

      return Number(value).toFixed(2);
    },
    formatGroupTotalDate(value) {
      if (!value) {
        return "Total";
      }

      const date = value instanceof Date ? value : new Date(value);
      if (Number.isNaN(date.getTime())) {
        return `Total ${value}`;
      }

      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();

      return `Total ${day}/${month}/${year}`;
    },
    normalizeCurrencyValue(value) {
      if (typeof value === "string") {
        const normalized = value
          .replace(this.currency, "")
          .replace(/\$/g, "")
          .replace(/,/g, "")
          .trim();
        const parsed = Number(normalized);
        return Number.isFinite(parsed) ? parsed : 0;
      }

      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    },
    normalizeFieldName(prefix, key) {
      return `${prefix}_${String(key)
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "")
        .toLowerCase()}`;
    },
    parsePaymentMap(value) {
      if (!value) {
        return {};
      }

      if (typeof value === "object") {
        return value;
      }

      try {
        const parsed = JSON.parse(value);
        return parsed && typeof parsed === "object" ? parsed : {};
      } catch (error) {
        return {};
      }
    },
    normalizeLocalDate(value) {
      if (!value) {
        return null;
      }

      if (value instanceof Date) {
        return value;
      }

      const dateMatch = String(value).trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (dateMatch) {
        const [, year, month, day] = dateMatch;
        return new Date(Number(year), Number(month) - 1, Number(day));
      }

      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? value : parsed;
    },
    extractPaymentColumns(paymentMaps, prefix) {
      const descriptors = [];
      const seen = new Set();

      paymentMaps.forEach((paymentMap) => {
        Object.keys(paymentMap || {}).forEach((key) => {
          if (seen.has(key)) {
            return;
          }

          seen.add(key);

          const [codePart, ...descriptionParts] = String(key).split(" - ");
          const numericCode = Number.parseInt(codePart, 10);
          const description = descriptionParts.join(" - ") || codePart;

          descriptors.push({
            key,
            code: Number.isNaN(numericCode) ? Number.MAX_SAFE_INTEGER : numericCode,
            description,
            caption: key,
            field: this.normalizeFieldName(prefix, key),
          });
        });
      });

      return descriptors.sort((left, right) => {
        if (left.code !== right.code) {
          return left.code - right.code;
        }

        return left.caption.localeCompare(right.caption);
      });
    },
    buildRows(payload) {
      const rawRows = Array.isArray(payload) ? payload : [];
      const parsedRows = rawRows.map((row) => ({
        ...row,
        Fecha: this.normalizeLocalDate(row.Fecha),
        TipoComprobante: row.TipoComprobante ?? row.InvoiceType ?? "",
        TotalAlojamiento: Number(row.TotalAlojamiento ?? 0),
        TotalConsumo: Number(row.TotalConsumo ?? 0),
        Gravado: Number(row.Gravado ?? 0),
        Exenta: Number(row.Exenta ?? 0),
        NoSujeta: Number(row.NoSujeta ?? 0),
        NoGravada: Number(row.NoGravada ?? 0),
        IVA: Number(row.IVA ?? 0),
        ImpuestoTurismo: Number(row.ImpuestoTurismo ?? 0),
        Total: Number(row.Total ?? 0),
        paymentsAlojamiento: this.parsePaymentMap(row.PagosAlojamiento),
        paymentsConsumo: this.parsePaymentMap(row.PagosConsumo),
      }));

      this.alojamientoPaymentColumns = this.extractPaymentColumns(
        parsedRows.map((row) => row.paymentsAlojamiento),
        "alojamiento"
      );

      this.consumoPaymentColumns = this.extractPaymentColumns(
        parsedRows.map((row) => row.paymentsConsumo),
        "consumo"
      );

      this.rows = parsedRows.map((row) => {
        const normalizedRow = { ...row };

        this.alojamientoPaymentColumns.forEach((column) => {
          normalizedRow[column.field] = Number(row.paymentsAlojamiento?.[column.key] ?? 0);
        });

        this.consumoPaymentColumns.forEach((column) => {
          normalizedRow[column.field] = Number(row.paymentsConsumo?.[column.key] ?? 0);
        });

        return normalizedRow;
      });
    },
    updateCurrentBranchLabel() {
      if (this.selectedBranch === "all") {
        this.currentBranchName = "Todas las sucursales";
        return;
      }

      const selected = this.branches.find((branch) => String(branch.ID_Branch) === String(this.selectedBranch));
      this.currentBranchName = selected ? selected.BranchName : "";
    },
    async loadBranches() {
      const user = JSON.parse(localStorage.getItem("user")) || {};
      const branchInfo = JSON.parse(localStorage.getItem("branch")) || {};

      try {
        const [allowedBranchesResponse, branchesResponse] = await Promise.all([
          this.$API.users.getAllowedBranches(user.id),
          this.$API.branches.findBranches(user.businessid),
        ]);

        const allowedBranches = Array.isArray(allowedBranchesResponse) ? allowedBranchesResponse : [];
        const allowedBranchIds = new Set(
          allowedBranches
            .map((branch) => Number(branch.ID_Branch))
            .filter((branchId) => Number.isInteger(branchId) && branchId > 0)
        );

        const availableBranches = Array.isArray(branchesResponse) ? branchesResponse : [];
        const filteredBranches = availableBranches.filter((branch) => allowedBranchIds.has(Number(branch.ID_Branch)));
        const currentBranchId = branchInfo.ID_Branch ?? user.branch ?? null;
        const currentBranch = filteredBranches.find((branch) => String(branch.ID_Branch) === String(currentBranchId));

        if (filteredBranches.length > 1) {
          this.branches = [
            { ID_Branch: "all", BranchName: "Todas las sucursales" },
            ...filteredBranches,
          ];
          this.selectedBranch = currentBranch ? currentBranch.ID_Branch : filteredBranches[0].ID_Branch;
          this.currentBranchName = currentBranch ? currentBranch.BranchName : filteredBranches[0].BranchName;
          return;
        }

        if (filteredBranches.length === 1) {
          this.branches = filteredBranches;
          this.selectedBranch = filteredBranches[0].ID_Branch;
          this.currentBranchName = filteredBranches[0].BranchName;
          return;
        }

        this.branches = currentBranchId
          ? [{ ID_Branch: currentBranchId, BranchName: branchInfo.BranchName || "" }]
          : [];
        this.selectedBranch = currentBranchId;
        this.currentBranchName = branchInfo.BranchName || "";
      } catch (error) {
        console.error("Error al cargar sucursales permitidas:", error);
        const currentBranchId = branchInfo.ID_Branch ?? user.branch ?? null;
        this.branches = currentBranchId
          ? [{ ID_Branch: currentBranchId, BranchName: branchInfo.BranchName || "" }]
          : [];
        this.selectedBranch = currentBranchId;
        this.currentBranchName = branchInfo.BranchName || "";
      }
    },
    getExcelFileName() {
      const modeLabel = this.showDetailedView ? "Vista detallada" : "Vista resumida";
      return `Libro de ventas - ${modeLabel}`;
    },
    onExporting(e) {
      e.cancel = true;
      const currencyFields = new Set([
        "TotalAlojamiento",
        "TotalConsumo",
        "Exenta",
        "NoSujeta",
        "NoGravada",
        "IVA",
        "ImpuestoTurismo",
        "Total",
        ...this.alojamientoPaymentColumns.map((column) => column.field),
        ...this.consumoPaymentColumns.map((column) => column.field),
      ]);

      printToExcel(e, this.getExcelFileName(), null, "Libro de ventas", true, true, null, {
        customizeCell: ({ gridCell, excelCell }) => {
          if (!gridCell || gridCell.rowType !== "data") {
            return;
          }

          if (!gridCell.column || !currencyFields.has(gridCell.column.dataField)) {
            return;
          }

          const numericValue = this.normalizeCurrencyValue(gridCell.value);
          excelCell.value = numericValue;
          excelCell.numFmt = '#,##0.00';
          excelCell.alignment = { horizontal: "right" };
        },
      });
    },
    async loadReport() {
      try {
        this.loading = true;

        const user = JSON.parse(localStorage.getItem("user")) || {};
        const params = {
          ID_Business: user.businessid,
          ID_Branch: this.selectedBranch === "all" ? null : this.selectedBranch,
          FechaInicio: this.parseDisplayDate(this.startDate),
          FechaFin: this.parseDisplayDate(this.endDate),
          IdentificacionAmbiente: this.dataType,
        };

        const response = await this.$API.accountingreports.getSalesHotelBookByDate(params);
        const payload = response?.data?.data ?? response?.data ?? [];

        this.buildRows(payload);
      } catch (error) {
        console.error("Error al obtener el reporte de ventas hotel:", error);
        this.rows = [];
        this.alojamientoPaymentColumns = [];
        this.consumoPaymentColumns = [];
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.report-grid-wrapper {
  width: 100%;
  overflow-x: auto;
}
</style>
